import React, {FC, useCallback, useEffect, useMemo, useState} from "react";
import {Button, Segmented, Spin, Tag, message} from "antd";
import {AppstoreOutlined, UnorderedListOutlined, CalendarOutlined} from "@ant-design/icons";
import {useGetCompanyInfoQuery, useGetLocationsQuery, useGetCourseLessonsForAssistantAppQuery, useAssignMyselfToCourseLessonAssistantAppMutation, useUnassignMyselfFromCourseLessonAssistantAppMutation, useGetAssistantAssignedLocationsQuery} from "../../generated/graphql";
import AssistantLargeTiles from "./AssistantLargeTiles";
import AssistantSmallTiles from "./AssistantSmallTiles";
import AssistantMonthCalendar from "./AssistantWeekCalendar";
import AssistantFilterBar from "./AssistantFilterBar";
import AssistantFilterDrawer from "./AssistantFilterDrawer";
import {useLastUpdated} from "./LastUpdatedContext";
import {startOfMonth, endOfMonth} from "date-fns";
import styles from "./assistantApp.less";

type ViewMode = "large" | "small" | "month";

const STORAGE_KEY = "assistant-home-view-mode";

function getInitialViewMode(): ViewMode {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === "large" || stored === "small" || stored === "month") {
            return stored;
        }
    } catch (_) {
        // localStorage may not be available
    }
    return "large";
}

const AssistantHome: FC = () => {
    const {setLastUpdated} = useLastUpdated();
    const {data: companyInfoData} = useGetCompanyInfoQuery({fetchPolicy: "cache-first"});
    const featureAssistantApp = companyInfoData?.companyInfo?.subscription?.featureAssistantApp;
    const {data: locationsData} = useGetLocationsQuery({fetchPolicy: "cache-first"});
    const [selectedCity, setSelectedCity] = useState<string | undefined>();
    const [selectedLocationId, setSelectedLocationId] = useState<string | undefined>();
    const [pendingLessonId, setPendingLessonId] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<ViewMode>(getInitialViewMode);
    const [monthDate, setMonthDate] = useState<Date>(() => startOfMonth(new Date()));
    const [selectedMonth, setSelectedMonth] = useState<Date | null>(null);
    const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);

    const {data: assignedLocationsData} = useGetAssistantAssignedLocationsQuery({
        fetchPolicy: "cache-first",
    });
    const assignedLocations = assignedLocationsData?.getAssistantAssignedLocations ?? [];
    const hasAssignedLocations = assignedLocations.length > 0;

    const handleViewModeChange = useCallback((value: string | number) => {
        const mode = value as ViewMode;
        setViewMode(mode);
        try {
            localStorage.setItem(STORAGE_KEY, mode);
        } catch (_) {
            // ignore
        }
    }, []);

    const handleMonthFilterChange = useCallback((month: Date | null) => {
        setSelectedMonth(month);
        if (month) setMonthDate(startOfMonth(month));
    }, []);

    const handleCalendarMonthChange = useCallback((newMonthDate: Date) => {
        setMonthDate(newMonthDate);
        if (selectedMonth) setSelectedMonth(startOfMonth(newMonthDate));
    }, [selectedMonth]);

    const filters = useMemo(() => {
        const base: {city?: string; locationId?: string; startDateTime?: string; endDateTime?: string} = {
            city: selectedCity || undefined,
            locationId: selectedLocationId || undefined,
        };
        if (selectedMonth) {
            base.startDateTime = startOfMonth(selectedMonth).toISOString();
            base.endDateTime = endOfMonth(selectedMonth).toISOString();
        } else if (viewMode === "month") {
            base.startDateTime = monthDate.toISOString();
            base.endDateTime = endOfMonth(monthDate).toISOString();
        }
        return base;
    }, [selectedCity, selectedLocationId, viewMode, monthDate, selectedMonth]);

    const {
        data: lessonsData,
        loading: lessonsLoading,
        error: lessonsError,
        refetch,
    } = useGetCourseLessonsForAssistantAppQuery({
        variables: {filters},
        skip: featureAssistantApp === false,
        fetchPolicy: "cache-and-network",
    });

    useEffect(() => {
        if (lessonsData && !lessonsLoading) {
            setLastUpdated(new Date());
        }
    }, [lessonsData, lessonsLoading, setLastUpdated]);

    const [assignLesson] = useAssignMyselfToCourseLessonAssistantAppMutation({
        onCompleted: () => {
            setPendingLessonId(null);
            refetch();
        },
        onError: (error) => {
            setPendingLessonId(null);
            message.error(error.message);
        },
    });

    const [unassignLesson] = useUnassignMyselfFromCourseLessonAssistantAppMutation({
        onCompleted: () => {
            setPendingLessonId(null);
            refetch();
        },
        onError: (error) => {
            setPendingLessonId(null);
            message.error(error.message);
        },
    });

    const handleAssign = useCallback((courseLessonId: string) => {
        setPendingLessonId(courseLessonId);
        assignLesson({variables: {courseLessonId}});
    }, [assignLesson]);

    const handleUnassign = useCallback((courseLessonId: string) => {
        setPendingLessonId(courseLessonId);
        unassignLesson({variables: {courseLessonId}});
    }, [unassignLesson]);

    const locations = locationsData?.locations ?? [];
    const cityOptions = Array.from(new Set(locations.map((location) => location.city).filter(Boolean))).map((city) => ({
        label: city as string,
        value: city as string,
    }));
    const locationOptions = locations.map((location) => ({
        label: `${location.name}${location.city ? ` (${location.city})` : ""}`,
        value: location.id,
    }));

    const lessons = lessonsData?.getCourseLessonsForAssistantApp ?? [];

    const selectedLocationLabel = useMemo(() => {
        if (!selectedLocationId) return undefined;
        const loc = locations.find((l) => l.id === selectedLocationId);
        return loc ? `${loc.name}${loc.city ? ` (${loc.city})` : ""}` : undefined;
    }, [selectedLocationId, locations]);

    if (featureAssistantApp === false) {
        return (
            <div className={styles.assistantFeatureDisabled}>
                <Tag color="volcano">{"Feature nicht freigeschalten"}</Tag>
            </div>
        );
    }

    return (
        <div>
            <div className={styles.assistantSectionHeader}>
                <div className={styles.assistantSectionTitle}>{"Verfügbare Dienste"}</div>
                <Segmented
                    value={viewMode}
                    onChange={handleViewModeChange}
                    options={[
                        {value: "large", icon: <AppstoreOutlined />},
                        {value: "small", icon: <UnorderedListOutlined />},
                        {value: "month", icon: <CalendarOutlined />},
                    ]}
                    size="middle"
                />
            </div>

            <AssistantFilterBar
                onOpenDrawer={() => setFilterDrawerVisible(true)}
                selectedCity={selectedCity}
                onClearCity={() => { setSelectedCity(undefined); setSelectedLocationId(undefined); }}
                selectedLocationId={selectedLocationId}
                selectedLocationLabel={selectedLocationLabel}
                onClearLocation={() => setSelectedLocationId(undefined)}
                selectedMonth={selectedMonth}
                onClearMonth={() => setSelectedMonth(null)}
                hasAssignedLocations={hasAssignedLocations}
            />

            <AssistantFilterDrawer
                open={filterDrawerVisible}
                onClose={() => setFilterDrawerVisible(false)}
                selectedCity={selectedCity}
                onCityChange={(v) => { setSelectedCity(v); setSelectedLocationId(undefined); }}
                cityOptions={cityOptions}
                selectedLocationId={selectedLocationId}
                onLocationChange={setSelectedLocationId}
                locationOptions={locationOptions}
                selectedMonth={selectedMonth}
                onMonthChange={handleMonthFilterChange}
            />

            {lessonsError && !lessonsLoading ? (
                <div className={styles.assistantEmpty}>
                    {"Fehler beim Laden. "}
                    <Button type="link" onClick={() => refetch()}>{"Erneut versuchen"}</Button>
                </div>
            ) : lessonsLoading && lessons.length === 0 ? (
                <div style={{textAlign: "center", padding: 48}}>
                    <Spin />
                </div>
            ) : lessons.length === 0 ? (
                <div className={styles.assistantEmpty}>{"Keine Kurseinheiten gefunden."}</div>
            ) : viewMode === "large" ? (
                <AssistantLargeTiles
                    lessons={lessons}
                    pendingLessonId={pendingLessonId}
                    onAssign={handleAssign}
                    onUnassign={handleUnassign}
                />
            ) : viewMode === "small" ? (
                <AssistantSmallTiles
                    lessons={lessons}
                    pendingLessonId={pendingLessonId}
                    onAssign={handleAssign}
                    onUnassign={handleUnassign}
                />
            ) : (
                <AssistantMonthCalendar
                    lessons={lessons}
                    pendingLessonId={pendingLessonId}
                    onAssign={handleAssign}
                    onUnassign={handleUnassign}
                    monthDate={monthDate}
                    onMonthChange={handleCalendarMonthChange}
                />
            )}
        </div>
    );
};

export default AssistantHome;
